import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { gbp } from "@/lib/stages";
import { ukDayYear } from "@/lib/time";
import { CellMain, CellSub, Empty, Kv, Note, Panel, RailCard, St, Tb, Td, Th } from "@/components/portal/crm/kit";

const num = { fontVariantNumeric: "tabular-nums" };

/** What an agent has earned on the cases they put in, under their company's internal split.
    Renders nothing at all until the account holder gives that seat a share. */
export function SeatEarnings({ variant = "rail" }) {
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get("/refer/seats/me/earnings").then((r) => setD(r.data)).catch(() => setD({ enabled: false }));
  }, []);

  if (!d?.enabled) return null;
  const company = d.company || "your company";

  if (variant === "rail") {
    return (
      <RailCard title="What you've earned" testId="seat-earnings">
        <Kv rows={[
          ["Your share", `${d.split_pct}% of the fee`],
          ["Earned so far", gbp(d.earned_gbp), { style: { fontWeight: 800 } }],
          ["Still to come", gbp(d.owed_gbp)],
          ["Settled", gbp(d.paid_gbp)],
        ]} />
        <div style={{ marginTop: 12, fontSize: 12, lineHeight: 1.5, color: "var(--mut)" }}>
          On the {d.cases} funded case{d.cases === 1 ? "" : "s"} you put in. {company} settles this with you —
          Connexis pays the company.
        </div>
      </RailCard>
    );
  }

  return (
    <Panel title="What you've earned" pad={false} testId="seat-earnings-full"
           sub={`${d.split_pct}% of the fee on every case you put in that funds. ${company} settles it with you — Connexis pays the company, not you directly.`}
           footer={<span>Figures move when a case funds and when {company} marks it settled. Anything you think is missing, ask the account holder first — they see the same table.</span>}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", borderBottom: "1px solid var(--line)" }}>
        {[["Earned so far", gbp(d.earned_gbp), "ink"], ["Still to come", gbp(d.owed_gbp), "mut"],
          ["Settled", gbp(d.paid_gbp), "green"], ["Funded cases", String(d.cases), "mut"]].map(([k, v, tone]) => (
          <div key={k} style={{ padding: "16px 18px" }}>
            <div style={{ fontSize: 10.5, letterSpacing: ".12em", textTransform: "uppercase", color: "var(--mut)", fontWeight: 700 }}>{k}</div>
            <div style={{ ...num, marginTop: 6, fontSize: 21, fontWeight: 800, color: tone === "green" ? "var(--green)" : tone === "mut" ? "var(--mut)" : "var(--ink)" }}>{v}</div>
          </div>
        ))}
      </div>
      {(d.rows || []).length === 0 ? (
        <Empty testId="seat-earnings-empty">Nothing yet. Your share shows here the moment one of your cases funds.</Empty>
      ) : (
        <Tb testId="seat-earnings-table" head={<>
          <Th style={{ width: "34%" }}>Case</Th>
          <Th style={{ width: "20%" }}>Finance</Th>
          <Th className="r" style={{ width: "16%" }}>Funded</Th>
          <Th style={{ width: "14%" }}>State</Th>
          <Th className="r" style={{ width: "16%" }}>Your share</Th>
        </>}>
          {(d.rows || []).map((r, i) => (
            <tr key={r.reference || i} data-testid={`seat-earnings-row-${i}`}>
              <Td className="lead">
                <CellMain>{r.business}</CellMain>
                <CellSub>{r.reference || "—"}</CellSub>
              </Td>
              <Td><CellMain>{r.finance || "Business funding"}</CellMain></Td>
              <Td className="r"><CellMain style={{ ...num, color: "var(--mut)", fontWeight: 500 }}>{ukDayYear(r.date, "—")}</CellMain></Td>
              <Td><St tone={r.settled ? "green" : "ink"}>{r.settled ? "Settled" : "To come"}</St></Td>
              <Td className="r"><CellMain style={{ ...num, fontWeight: 800 }}>{gbp(r.your_gbp)}</CellMain></Td>
            </tr>
          ))}
        </Tb>
      )}
      <div style={{ padding: "14px 18px 18px" }}>
        <Note testId="seat-earnings-note">
          This is an internal split agreed with {company}. Connexis has no contract with you on money — your seat is
          there so the cases you put in are credited to you by name.
        </Note>
      </div>
    </Panel>
  );
}
